import React from 'react';
import { Select } from '../../ui/primitives/Select';
import type { SelectOptionGroup } from '../../ui/primitives/Select';
import { Tag } from '../../ui/primitives/Tag';
import { Stack } from '../../ui/primitives/Stack';
import { DirectDebitRun } from '../../model/payments/DirectDebitRun';
import {
    groupDirectDebitRuns,
    getRunStatusLabel,
    getRunStatusTone,
} from './directDebitRunGrouping';

interface DirectDebitRunSelectProps {
    runs: DirectDebitRun[];
    selectedRun: DirectDebitRun | null;
    onSelect: (run: DirectDebitRun) => void;
}

// Wide enough for the longest date plus the "Rejections processed" pill.
const SELECT_WIDTH = 340;

const runDateLabel = (run: DirectDebitRun): string => new Date(run.paymentDate).toDateString();

// Options are keyed on runId rather than the rendered date: the value only has
// to round-trip back to a run, and a date string would mean re-parsing a
// locale-formatted label to find it again.
const toGroupedOptions = (runs: DirectDebitRun[]): SelectOptionGroup[] =>
    groupDirectDebitRuns(runs).map(group => ({
        label: group.label,
        options: group.runs.map(run => {
            const date = runDateLabel(run);
            const statusLabel = getRunStatusLabel(run.status);
            return {
                value: run.runId,
                // Shown in the closed control (optionLabelProp="title"), where
                // the status pill would just be noise.
                title: date,
                label: (
                    <Stack direction="horizontal" align="center" justify="space-between">
                        <span data-testid={`dd-run-option-${run.runId}`}>{date}</span>
                        {statusLabel && (
                            <Tag tone={getRunStatusTone(run.status)}>{statusLabel}</Tag>
                        )}
                    </Stack>
                ),
            };
        }),
    }));

export const DirectDebitRunSelect: React.FC<DirectDebitRunSelectProps> = ({
    runs,
    selectedRun,
    onSelect,
}) => (
    <Select
        options={toGroupedOptions(runs)}
        optionLabelProp="title"
        value={selectedRun?.runId}
        onChange={value => {
            const run = runs.find(r => r.runId === value);
            if (run) onSelect(run);
        }}
        placeholder="Select a Direct Debit Run"
        aria-label="Direct debit run"
        style={{ width: SELECT_WIDTH }}
        data-testid="dd-run-select"
    />
);
